/**
 * Live “is typing…” presence for CRM comment composers.
 *
 * Each typist writes `commentTyping/{threadKey}/typists/{uid}`. Peers subscribe
 * and drop docs older than {@link COMMENT_TYPING_STALE_MS} (closed tabs).
 */
import { db } from './firebase';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

export const COMMENT_TYPING_STALE_MS = 8_000;
export const COMMENT_TYPING_HEARTBEAT_MS = 2_000;

export type CommentTypingThreadKind = 'task' | 'group' | 'lead' | 'ops' | 'marketing';

export interface CommentTypist {
  uid: string;
  name: string;
  updatedAt: Date | null;
}

export function commentTypingThreadKey(
  kind: CommentTypingThreadKind,
  ...parts: string[]
): string {
  return [kind, ...parts.filter(Boolean)].join(':');
}

export function firstNameFromDisplayName(displayName: string | undefined | null): string {
  const first = (displayName || '').trim().split(/\s+/)[0];
  return first || 'Someone';
}

export function isFreshCommentTypist(t: CommentTypist, now = Date.now()): boolean {
  return !!t.updatedAt && now - t.updatedAt.getTime() < COMMENT_TYPING_STALE_MS;
}

function typistDoc(threadKey: string, uid: string) {
  return doc(db, 'commentTyping', threadKey, 'typists', uid);
}

export function formatCommentTypingLabel(names: string[]): string {
  const unique = names.map(n => n.trim()).filter(Boolean);
  if (unique.length === 0) return '';
  if (unique.length === 1) return `${unique[0]} is typing…`;
  if (unique.length === 2) return `${unique[0]} and ${unique[1]} are typing…`;
  if (unique.length === 3) return `${unique[0]}, ${unique[1]}, and ${unique[2]} are typing…`;
  const extra = unique.length - 2;
  return `${unique[0]}, ${unique[1]}, and ${extra} others are typing…`;
}

export function subscribeCommentTyping(
  threadKey: string,
  selfUid: string,
  callback: (typists: CommentTypist[]) => void,
): () => void {
  if (!threadKey || !selfUid) {
    callback([]);
    return () => undefined;
  }
  return onSnapshot(
    collection(db, 'commentTyping', threadKey, 'typists'),
    snap => {
      const typists = snap.docs
        .map(d => {
          const v = d.data();
          const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
          return {
            uid: d.id,
            name: firstNameFromDisplayName(typeof v.name === 'string' ? v.name : ''),
            updatedAt: updatedAtRaw?.toDate?.() ?? null,
          };
        })
        .filter(t => t.uid !== selfUid && isFreshCommentTypist(t));
      callback(typists);
    },
    err => {
      console.warn('[commentTypingService] subscribe failed', err);
      callback([]);
    },
  );
}

export async function publishCommentTyping(
  threadKey: string,
  uid: string,
  name: string,
): Promise<void> {
  if (!threadKey || !uid) return;
  try {
    await setDoc(
      typistDoc(threadKey, uid),
      {
        name: firstNameFromDisplayName(name),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[commentTypingService] publish failed', err);
  }
}

export async function clearCommentTyping(threadKey: string, uid: string): Promise<void> {
  if (!threadKey || !uid) return;
  try {
    await deleteDoc(typistDoc(threadKey, uid));
  } catch {
    /* stale-filtering handles missed deletes */
  }
}
